/**
 * Utility functions for handling image operations in the chat app
 */

/**
 * Generates the full URL for an image based on the image path
 * @param {string} imagePath - The relative path to the image
 * @param {string} baseUrl - The base URL of the API server (should be passed from component using config.apiUrl)
 * @returns {string} The full URL to the image
 */
import config from '../config';

export const getImageUrl = (imagePath, baseUrl = null) => {
  // Use config.apiUrl as default if baseUrl not provided
  if (!baseUrl) {
    baseUrl = config.apiUrl;
  }
  
  if (!imagePath) return '';
  
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Ensure the path starts with /
  const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  
  return `${baseUrl}${cleanPath}`;
};

/**
 * Opens an image in the fullscreen viewer
 * @param {string} imageUrl - The URL of the image to view
 * @param {Function} setViewerState - Function to set the viewer state
 * @param {string} baseUrl - The base URL of the API server (should be passed from component using config.apiUrl)
 */
export const openImageViewer = (imageUrl, setViewerState, baseUrl = null) => {
  setViewerState({
    isOpen: true,
    imageUrl: getImageUrl(imageUrl, baseUrl)
  });
};

/**
 * Closes the fullscreen image viewer
 * @param {Function} setViewerState - Function to set the viewer state
 */
export const closeImageViewer = (setViewerState) => {
  setViewerState({
    isOpen: false,
    imageUrl: ''
  });
};

/**
 * Validates if a file is a valid image
 * @param {File} file - The file to validate
 * @returns {boolean} Whether the file is a valid image
 */
export const isValidImage = (file) => {
  if (!file) return false;
  
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  return validTypes.includes(file.type);
};

/**
 * Validates image file size
 * @param {File} file - The file to validate
 * @param {number} maxSizeMB - Maximum size in MB (default: 5)
 * @returns {boolean} Whether the file size is valid
 */
export const isValidImageSize = (file, maxSizeMB = 5) => {
  if (!file) return false;
  
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Formats file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Creates a preview URL for a file
 * @param {File} file - The file to create preview for
 * @returns {string} The preview URL
 */
export const createImagePreview = (file) => {
  if (!file || !isValidImage(file)) return '';
  
  return URL.createObjectURL(file);
};

/**
 * Revokes a preview URL to free up memory
 * @param {string} url - The URL to revoke
 */
export const revokeImagePreview = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};
